import { useState } from "react";
import {
  Card,
  CardBody,
  Chip,
  Button,
  CardHeader,
  CardFooter,
  Progress,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  addToast,
  Input,
  RadioGroup,
  Radio,
  useDisclosure,
} from "@heroui/react";
import { LuSettings, LuCreditCard, LuClock } from "react-icons/lu";
import {
  getLocalizedName,
  getLocalizedValue,
  getTranslatedTimeframe,
} from "@/utils";
import { GetCustomerSubscriptions } from "@/server-types";
import { SubscriptionInvoiceModal } from "@/Components/SubscriptionInvoiceModal";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import dayjs from "dayjs";
import { i18n } from "@lingui/core";
import { api, queryClient } from "@/lib/api-client";
import { CustomerSubscriptionEventsModal } from "./CustomerSubscriptionEventsModal";

const getStatusTranslation = (
  status: GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"][number]["status"],
) => {
  switch (status) {
    case "active":
      return t`Aktivna`;
    case "frozen":
      return t`Zamrznjena`;
    case "canceled":
      return t`Preklicana`;
    default:
      return "";
  }
};

export const CustomerSubscriptionCard = ({
  customerSubscription,
  onManage,
}: {
  customerSubscription: GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"][number];
  onManage: (subscriptionId: number) => void;
}) => {
  const {
    isOpen: isInvoiceModalOpen,
    onOpen: onInvoiceModalOpen,
    onClose: onInvoiceModalClose,
  } = useDisclosure();

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const {
    isOpen: isExtendModalOpen,
    onOpen: onExtendModalOpen,
    onClose: onExtendModalClose,
  } = useDisclosure();

  const {
    isOpen: isFreezeModalOpen,
    onOpen: onFreezeModalOpen,
    onClose: onFreezeModalClose,
  } = useDisclosure();

  const {
    isOpen: isEventsModalOpen,
    onOpen: onEventsModalOpen,
    onClose: onEventsModalClose,
  } = useDisclosure();

  const {
    isOpen: isUnfreezeModalOpen,
    onOpen: onUnfreezeModalOpen,
    onClose: onUnfreezeModalClose,
  } = useDisclosure();

  const [extensionDate, setExtensionDate] = useState("");

  const { mutateAsync: updateCustomerSubscription } =
    api.customer.useUpdateCustomerSubscription();
  const { locale } = i18n;
  const { mutate: cancelSubscription } =
    api.customer.useCancelCustomerSubscription();

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription({
        customerSubscriptionId: customerSubscription.id,
      });
      addToast({
        title: t`Naročnina preklicana`,
        description: t`Naročnina je bila uspešno preklicana`,
        color: "success",
      });
      onCancelModalClose();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri preklicu naročnine`,
        color: "danger",
      });
    }
  };

  const handleExtendSubscription = async () => {
    console.log(
      `Extending subscription ${customerSubscription.id} until: ${extensionDate}`,
    );

    try {
      await updateCustomerSubscription({
        customerSubscriptionId: customerSubscription.id,
        body: {
          validTo: new Date(extensionDate),
          eventType: "extension",
        },
      });
      addToast({
        title: t`Naročnina podaljšana`,
        description: t`Naročnina je bila uspešno podaljšana do ${new Date(extensionDate).toLocaleDateString(locale)}`,
        color: "success",
      });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri podaljšanju naročnine`,
        color: "danger",
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["customer-subscription"],
    });

    onExtendModalClose();
  };

  const handleFreezeSubscription = async () => {
    try {
      await updateCustomerSubscription({
        customerSubscriptionId: customerSubscription.id,
        body: { eventType: "freezeStart", status: "frozen" },
      });

      addToast({
        title: t`Naročnina zamrznjena`,
        description: t`Naročnina je bila uspešno zamrznjena`,
        color: "success",
      });

      onFreezeModalClose();

      queryClient.invalidateQueries({
        queryKey: ["customer-subscription"],
      });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri zamrzovanju naročnine`,
        color: "danger",
      });
    }
  };

  const handleUnfreezeSubscription = async () => {
    try {
      await updateCustomerSubscription({
        customerSubscriptionId: customerSubscription.id,
        body: { eventType: "freezeEnd", status: "active" },
      });

      addToast({
        title: t`Naročnina odmrznjena`,
        description: t`Naročnina je bila uspešno odmrznjena`,
        color: "success",
      });

      onUnfreezeModalClose();

      queryClient.invalidateQueries({
        queryKey: ["customer-subscription"],
      });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri odmrznitvi naročnine`,
        color: "danger",
      });
    }
  };
  return (
    <>
      <Card
        fullWidth
        shadow="none"
        radius="md"
        data-identifier="subscription-card"
      >
        <CardHeader className="flex items-center justify-between">
          <Chip
            data-identifier="subscription-status"
            size="sm"
            variant="bordered"
            radius="sm"
            color={
              customerSubscription.status === "active" ? "primary" : "warning"
            }
          >
            {getStatusTranslation(customerSubscription.status)}
          </Chip>
          <p
            data-identifier="subscription-price"
            className="text-lg font-semibold"
          >
            {customerSubscription.priceFormatted}
          </p>
        </CardHeader>

        <CardBody className="flex flex-col gap-2">
          <h3
            data-identifier="customer-subscription-name"
            className="text-xl font-semibold"
          >
            {customerSubscription.subscription ? (
              <>
                <span data-identifier="subscription-name">
                  {getLocalizedValue(
                    customerSubscription.subscription.nameLocalized,
                  )}
                </span>
                {customerSubscription.variation && (
                  <>
                    <span> - </span>
                    <span data-identifier="subscription-variation">
                      {getLocalizedValue(
                        customerSubscription.variation.nameLocalized,
                      )}
                    </span>
                  </>
                )}
              </>
            ) : (
              ""
            )}
          </h3>

          <div className="flex flex-col gap-1">
            <p data-identifier="subscription-duration" className="text-small">
              <Trans>Trajanje</Trans>
              {": "}
              <span className="font-medium">
                {customerSubscription.durationValue}{" "}
                {getTranslatedTimeframe(customerSubscription.durationUnit)}
              </span>
            </p>
            <p
              data-identifier="subscription-valid-from"
              className="text-small text-gray-500"
            >
              <Trans>Od</Trans>
              {": "}{" "}
              {dayjs(customerSubscription.validFrom)
                .toDate()
                .toLocaleDateString(locale)}
            </p>
            <p
              data-identifier="subscription-valid-to"
              className="text-small text-gray-500"
            >
              <Trans>Do</Trans>
              {": "}
              {dayjs(customerSubscription.validTo)
                .toDate()
                .toLocaleDateString(locale)}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {customerSubscription.customerSubscriptionServices.map(
              (subscriptionService) => (
                <div key={subscriptionService.id}>
                  <p
                    data-identifier="subscription-service-name"
                    className="text-medium font-semibold"
                  >
                    {getLocalizedName(
                      subscriptionService.service.nameLocalized,
                    )}
                  </p>
                  <div>
                    {subscriptionService.limitations.length > 0 ? (
                      subscriptionService.limitations.map((limitation) => {
                        const percentage =
                          (limitation.usedAmount / limitation.limitAmount) *
                          100;
                        const remaining =
                          limitation.limitAmount - limitation.usedAmount;

                        return (
                          <div
                            key={`${subscriptionService.service.serviceId}-${limitation.timeframe}`}
                            className="space-y-2"
                          >
                            <div
                              data-identifier="subscription-service-limitation"
                              className="flex justify-between text-small"
                            >
                              <span className="text-default-500">
                                <Trans>
                                  {limitation.usedAmount} od{" "}
                                  {limitation.limitAmount} obiskov (
                                  {getTranslatedTimeframe(limitation.timeframe)}
                                  )
                                </Trans>
                              </span>
                              <span
                                data-identifier="subscription-service-remaining"
                                className="text-default-500"
                              >
                                <Trans>{remaining} preostalih</Trans>
                              </span>
                            </div>
                            <Progress
                              data-identifier="subscription-service-progress"
                              value={percentage}
                              size="sm"
                            />
                          </div>
                        );
                      })
                    ) : (
                      <p
                        data-identifier="subscription-service-unlimited"
                        className="text-small text-default-500"
                      >
                        <Trans>Neomejeni obiski</Trans>
                      </p>
                    )}
                  </div>
                </div>
              ),
            )}
            {customerSubscription.limitations.length > 0 && (
              <p
                data-identifier="subscription-total-visits"
                className="text-medium font-semibold"
              >{t`Skupni obiski`}</p>
            )}
            {customerSubscription.limitations.map((limitation) => {
              const percentage =
                (limitation.usedAmount / limitation.limitAmount) * 100;
              const remaining = limitation.limitAmount - limitation.usedAmount;

              return (
                <div
                  key={`limitation-${limitation.timeframe}`}
                  className="space-y-2"
                >
                  <div
                    data-identifier="subscription-total-limitation"
                    className="flex justify-between text-small"
                  >
                    <span className="text-default-500">
                      <Trans>
                        {limitation.usedAmount} od {limitation.limitAmount}{" "}
                        obiskov ({getTranslatedTimeframe(limitation.timeframe)})
                      </Trans>
                    </span>
                    <span
                      data-identifier="subscription-total-remaining"
                      className="text-default-500"
                    >
                      <Trans>{remaining} preostalih</Trans>
                    </span>
                  </div>
                  <Progress
                    data-identifier="subscription-total-progress"
                    value={percentage}
                    size="sm"
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <p
              data-identifier="subscription-future-appointments"
              className="text-medium font-semibold"
            >{t`Prihodnji termini`}</p>
            <Chip
              data-identifier="subscription-future-appointments-count"
              size="sm"
              variant="flat"
              radius="sm"
              color="primary"
            >
              {customerSubscription.futureAppointmentsCount}
            </Chip>
          </div>
        </CardBody>
        <CardFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {customerSubscription.subscription?.tags.map((tag) => (
              <Chip
                key={tag.id}
                data-identifier="subscription-tag"
                size="sm"
                variant="flat"
                radius="sm"
              >
                {getLocalizedValue(tag.nameLocalized)}
              </Chip>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={onInvoiceModalOpen}
            >
              <LuCreditCard className="text-xl" />
            </Button>
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={onEventsModalOpen}
              title={t`Preglej dogodke`}
            >
              <LuClock className="text-xl" />
            </Button>
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly variant="light" size="sm">
                  <LuSettings className="text-xl" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Subscription actions">
                <DropdownItem
                  key="edit"
                  onPress={() => onManage(customerSubscription.id)}
                >
                  <Trans>Uredi naročnino</Trans>
                </DropdownItem>
                <DropdownItem key="extend" onPress={onExtendModalOpen}>
                  <Trans>Podaljšaj naročnino</Trans>
                </DropdownItem>

                {customerSubscription.status === "frozen" ? (
                  <DropdownItem key="unfreeze" onPress={onUnfreezeModalOpen}>
                    <Trans>Odmrzni naročnino</Trans>
                  </DropdownItem>
                ) : (
                  <DropdownItem key="freeze" onPress={onFreezeModalOpen}>
                    <Trans>Zamrzni naročnino</Trans>
                  </DropdownItem>
                )}

                <DropdownItem
                  key="cancel"
                  className="text-danger"
                  color="danger"
                  onPress={onCancelModalOpen}
                >
                  <Trans>Prekliči naročnino</Trans>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </CardFooter>
      </Card>

      <Modal isOpen={isExtendModalOpen} onClose={onExtendModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Podaljšanje naročnine</Trans>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p>
                <Trans>
                  Trenutna naročnina je veljavna do:{" "}
                  {dayjs(customerSubscription.validTo).format("DD.MM.YYYY")}
                </Trans>
              </p>
              <div>
                <label
                  htmlFor="extension-date"
                  className="mb-2 block text-sm font-medium"
                >
                  <Trans>Podaljšaj do</Trans>
                </label>
                <Input
                  id="extension-date"
                  type="date"
                  value={extensionDate}
                  onChange={(e) => setExtensionDate(e.target.value)}
                  min={dayjs(customerSubscription.validTo).format("YYYY-MM-DD")}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onExtendModalClose}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={handleExtendSubscription}
              isDisabled={!extensionDate}
            >
              <Trans>Podaljšaj</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isFreezeModalOpen} onClose={onFreezeModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Zamrznitev naročnine</Trans>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p>
                <Trans>Ste prepričani, da želite zamrzniti to naročnino?</Trans>
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onFreezeModalClose}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={handleFreezeSubscription}
            >
              <Trans>Zamrzni</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SubscriptionInvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={onInvoiceModalClose}
        customerSubscriptionId={customerSubscription.id}
      />

      <Modal isOpen={isCancelModalOpen} onClose={onCancelModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Preklic naročnine</Trans>
          </ModalHeader>
          <ModalBody>
            <p>
              <Trans>
                Ali ste prepričani, da želite preklicati to naročnino? Te akcije
                ni mogoče razveljaviti.
              </Trans>
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onCancelModalClose}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              color="danger"
              variant="solid"
              onPress={handleCancelSubscription}
            >
              <Trans>Potrdi preklic</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CustomerSubscriptionEventsModal
        isOpen={isEventsModalOpen}
        onClose={onEventsModalClose}
        events={customerSubscription.events || []}
      />

      <Modal isOpen={isUnfreezeModalOpen} onClose={onUnfreezeModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Odmrznitev naročnine</Trans>
          </ModalHeader>
          <ModalBody>
            <p>
              <Trans>Ste prepričani, da želite odmrzniti to naročnino?</Trans>
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onUnfreezeModalClose}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={handleUnfreezeSubscription}
            >
              <Trans>Odmrzni</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
